import { useAuth } from "react-oidc-context";
import { useTranslation } from "react-i18next";
import Alert from "../Alert";
import Button from "../Button";
import { getIsWebView } from "../../utils/webView";
import {
  AuthenticatedContainer,
  ButtonWrapper,
  LoadingContainer,
} from "./styles";

/**
 * D3 OIDC Login Component for SPL
 *
 * SPL-specific implementation using D3 OIDC:
 * - Uses SPL's Button component and styled-components
 * - Matches SPL's FirebaseLoginLinks styling patterns
 * - Handles D3 OIDC authentication flow
 * - Maintains webview detection
 * - Displays loading, error, and authenticated states
 */

export const D3Login = () => {
  const auth = useAuth();
  const { t } = useTranslation();

  // Don't show login in webview (app handles auth)
  if (getIsWebView()) {
    return null;
  }

  const handleLogin = () => {
    auth.signinRedirect();
  };

  const handleRegister = () => {
    // D3 treats register same as login - they handle the flow
    auth.signinRedirect();
  };

  // Show loading state
  if (auth.isLoading || auth.activeNavigator) {
    return (
      <LoadingContainer>
        <p>{t("login.loading", "Loading...")}</p>
      </LoadingContainer>
    );
  }

  // Show OIDC errors (e.g., user cancelled, D3 misconfiguration)
  if (auth.error) {
    return (
      <>
        <Alert type="error">
          <strong>
            {t("login.error.authenticationFailed", "Authentication Failed")}
          </strong>
          <p>{auth.error.message}</p>
        </Alert>
        <ButtonWrapper>
          <Button width={1} onClick={handleLogin} variant="primary">
            {t("login.loginButton", "Login")}
          </Button>

          <Button width={1} onClick={handleRegister} variant="secondary">
            {t("login.promo.signUpNow", "Sign Up Now")}
          </Button>
        </ButtonWrapper>
      </>
    );
  }

  // Show authenticated state
  if (auth.isAuthenticated && auth.user) {
    return (
      <AuthenticatedContainer>
        <p>
          {t("login.loggedInAs", "You are logged in as")}{" "}
          <strong>{auth.user.profile.email || auth.user.profile.sub}</strong>
        </p>
      </AuthenticatedContainer>
    );
  }

  // Show login/register buttons
  return (
    <ButtonWrapper>
      <Button width={1} onClick={handleLogin} variant="primary">
        {t("login.loginButton", "Login")}
      </Button>

      <Button width={1} onClick={handleRegister} variant="secondary">
        {t("login.promo.signUpNow", "Sign Up Now")}
      </Button>
    </ButtonWrapper>
  );
};
